import { createContext, useContext, ReactNode } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';

interface NavigationContextType {
  getBackNavigation: () => { path: string; label: string } | null;
  getBreadcrumbs: () => { path: string; label: string }[];
}

const NavigationContext = createContext<NavigationContextType | null>(null);

export const useNavigationContext = () => {
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const getBackNavigation = () => {
    const from = searchParams.get('from');
    const tagId = searchParams.get('tagId');
    const tagName = searchParams.get('tagName');

    switch (from) {
      case 'tag':
        return {
          path: `/tags/${tagId}`,
          label: `Back to ${tagName || 'Tag'}`
        };
      case 'dashboard':
        return {
          path: '/dashboard',
          label: 'Back to Dashboard'
        };
      default:
        // Default fallback based on current path
        if (location.pathname.includes('/devices/')) {
          return {
            path: '/devices',
            label: 'Back to Devices'
          };
        }
        return null;
    }
  };

  const getBreadcrumbs = () => {
    const breadcrumbs: { path: string; label: string }[] = [];
    const from = searchParams.get('from');
    const tagId = searchParams.get('tagId');
    const tagName = searchParams.get('tagName');

    if (from === 'tag' && tagId && tagName) {
      breadcrumbs.push({ path: '/tags', label: 'Tags' });
      breadcrumbs.push({ path: `/tags/${tagId}`, label: tagName });
      if (location.pathname.includes('/devices/')) {
        breadcrumbs.push({ path: `/tags/${tagId}?tab=devices`, label: 'Devices' });
      }
    } else if (location.pathname.includes('/devices/')) {
      breadcrumbs.push({ path: '/devices', label: 'Devices' });
    }

    return breadcrumbs;
  };

  return {
    getBackNavigation,
    getBreadcrumbs
  };
};

export const NavigationProvider = ({ children }: { children: ReactNode }) => {
  return (
    <NavigationContext.Provider value={null}>
      {children}
    </NavigationContext.Provider>
  );
};